import React, { useMemo, useRef, useState } from 'react';
import './ChildrensBookChapterReadabilityV1E4G.css';
import ChildrensBookNovelBookViewReuseV1E4C from './ChildrensBookNovelBookViewReuseV1E4C';
import ChildrensBookManuscriptPlan from './ChildrensBookManuscriptPlan';
import NextScenesBookBlueprintNavigator from './NextScenesBookBlueprintNavigator';
import ChildrensBookChapterSceneNavigatorV1 from './ChildrensBookChapterSceneNavigatorV1';
import { addChildrensBookChapterV1E4T, addChildrensBookSceneV1E4T } from '../utils/childrensBookAuthorJourneyV1E4T';
import { buildChildrensBookChapterSceneNavigationV1 } from '../utils/childrensBookChapterSceneNavigationV1';
import { resolveChildrensBookNovelDestinationV1E3 } from '../utils/childrensBookNovelRouteBridgeV1E3';
import ChildrensBookSceneFirstWriterV1E5C from './ChildrensBookSceneFirstWriterV1E5C';
import ChildrensBookSceneFirstMatterV1E6B2 from './ChildrensBookSceneFirstMatterV1E6B2';
import {sceneFirstEligibleV1E5C, sceneFirstNavigationV1E5C, addSceneFirstChapterV1E5C,
  addSceneFirstSceneV1E5C, changeSceneFirstTextV1E5C,
  renameSceneFirstChapterV1E6A, moveSceneFirstChapterV1E6A} from '../utils/childrensBookSceneFirstAuthorV1E5C';

// V1E4D: NO new Book Workspace, Chapter cards, Scene cards or Writer Studio Path.
// Genuine Novel Book View is presentation-only; the REAL Children's Book editor
// and Blueprint callbacks exclusively own manuscript edits and persistence.
const clean = (value) => String(value ?? '').trim();
export default function ChildrensBookNovelIntegratedWorkspaceV1E4D({
  draft = {}, lang='en', readOnly=false, childrensBookAssets=[],
  onLoadChildrensBookAssetFile, onChangeManuscript, onPositionChange,
  onOpenExistingWorkspace, onExit, hasUnsavedChanges=false,
  savedAudienceReady=false,
}) {
  const fr = String(lang).toLowerCase().startsWith('fr');
  const units = useMemo(() => Array.isArray(draft?.bookMap?.units) ? draft.bookMap.units : [], [draft?.bookMap?.units]);
  const outline = draft?.manuscriptPlan?.chapterSceneOutline;
  const savedChapters = useMemo(() =>
    (outline?.version === 1 || outline?.version === 2) && Array.isArray(outline?.chapters)
      ? outline.chapters : [], [outline]);
  const sceneFirstWriterEnabled = sceneFirstEligibleV1E5C(draft);
  const navigation = useMemo(() => sceneFirstWriterEnabled
    ? sceneFirstNavigationV1E5C(draft)
    : buildChildrensBookChapterSceneNavigationV1({units, chapters:savedChapters}),
    [draft,units,savedChapters,sceneFirstWriterEnabled]);
  const chapterSections = navigation?.ok ? navigation.sections.filter(row => row.kind === 'chapter') : [];
  const unassignedUnits = navigation?.ok ? navigation.sections
    .filter(row => row.kind === 'unassigned').flatMap(row => row.entries || []) : [];
  const [destination,setDestination] = useState(null);
  const [chooser,setChooser] = useState(null);
  const [studioStage,setStudioStage] = useState('write');
  const [notice,setNotice] = useState('');
  const [newSceneChapterId,setNewSceneChapterId] = useState('');
  const [newSceneUnitId,setNewSceneUnitId] = useState('');
  const [newSceneTitle,setNewSceneTitle] = useState('');
  const [structureError,setStructureError] = useState('');
  const [bookReturnTarget,setBookReturnTarget] = useState(null);
  const lastUnitId=useRef('');
  const title=clean(draft?.identity?.title) || (fr?'Livre jeunesse':'Children’s Book');
  const canWrite = !readOnly && savedAudienceReady;
  const activeChapter = chapterSections.find(row => clean(row.key) === clean(destination?.chapterId)) || null;
  // The chooser has no active writer destination yet: resolve its own saved Chapter.
  const chooserChapter = chooser
    ? chapterSections.find(row => clean(row.key) === clean(chooser.chapterId)) || null
    : null;
  const activeScenes = Array.isArray(activeChapter?.scenes) ? activeChapter.scenes : [];
  const activeScene = activeScenes.find(row => clean(row.key) === clean(destination?.sceneId)) || null;
  const chapterIndex = activeChapter ? chapterSections.findIndex(row => row.key === activeChapter.key) : -1;
  const returnLabel = activeChapter
    ? (fr ? `← Retour à ${activeChapter.title || 'ce chapitre'}` : `← Back to ${activeChapter.title || 'Chapter'}`)
    : (fr ? '← Retour au livre' : '← Back to Book');
  const returnToBook=()=>{
    if (destination?.stage === 'scene-first-writer') {
      const chapterId=destination.chapterId;
      setDestination(null);setChooser(null);setNotice('');
      setBookReturnTarget(current=>({sequence:(current?.sequence||0)+1,stage:'chapter-review',chapterId}));
      return;
    }
    // Resolve current physical unit, not the Scene's original first page.
    const result=lastUnitId.current
      ? resolveChildrensBookNovelDestinationV1E3({navigation,unitId:lastUnitId.current})
      : null;
    setDestination(null);setChooser(null);setNotice('');
    if (result?.ok && (result.returnTo?.stage === "chapter-review" || result.returnTo?.sectionKind)) {
      setBookReturnTarget(current=>({sequence:(current?.sequence||0)+1,...result.returnTo}));
      onPositionChange?.({unitId:lastUnitId.current,returnSection:result.returnTo.sectionKind,
        returnChapterId:result.returnTo.chapterId || ''});
    }
  };
  const returnFromChooser=()=>{
    const chapterId = chooserChapter?.key;
    setChooser(null);setNotice('');
    if (chapterId) {
      // Return to the exact Chapter that opened this physical-unit chooser.
      // No page is selected, written or inferred by this action.
      setBookReturnTarget(current=>({sequence:(current?.sequence||0)+1,
        stage:'chapter-review',chapterId}));
    }
  };
  const openUnit=(unitId)=>{
    const result=resolveChildrensBookNovelDestinationV1E3({navigation,unitId});
    if (!result?.ok || result.stage!=='writer-studio' || !units.some(x=>clean(x?.id)===unitId)){
      setNotice(fr?'Page ou double page introuvable.':'Page or spread not found.');return;
    }
    lastUnitId.current=unitId;
    setDestination(result);setChooser(null);setStudioStage(canWrite?'write':'review');setNotice('');
    onPositionChange?.({unitId});
  };
  const openScene=({chapterId,sceneId})=>{
    if (sceneFirstWriterEnabled) {
      const chapter=chapterSections.find(row=>row.key===chapterId);
      if (!chapter?.scenes?.some(row=>row.key===sceneId)) {setNotice(fr?'Scène introuvable.':'Unknown Scene.');return;}
      setDestination({stage:'scene-first-writer',chapterId,sceneId});setChooser(null);setNotice('');
      return;
    }
    const result=resolveChildrensBookNovelDestinationV1E3({navigation,chapterId,sceneId});
    if(!result?.ok){setNotice(fr?'Scène inconnue.':'Unknown Scene.');return;}
    if(result.stage==='choose-page-or-spread'){
      setDestination(null);setChooser(result);setNotice('');return;
    }
    if(result.stage==='writer-studio')openUnit(result.unitId);
  };
  const navigateToChapter = chapterId => {
    const target = chapterSections.find(row => clean(row.key) === clean(chapterId));
    if (!target) return;
    const first = Array.isArray(target.scenes) ? target.scenes[0] : null;
    if (first?.key) {
      openScene({chapterId:target.key,sceneId:first.key});
      return;
    }
    // An existing empty Chapter remains empty; do not invent its first Scene.
    setDestination(null);setChooser(null);setNotice('');
    setBookReturnTarget(current=>({sequence:(current?.sequence||0)+1,
      stage:'chapter-review',chapterId:target.key}));
  };
  const newId = prefix => prefix + '-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2,10);
  const addChapter = () => {
    if (readOnly) return;
    const id=newId('cb-chapter');
    const title=(fr?'Chapitre ':'Chapter ') + (savedChapters.length+1);
    const result = sceneFirstWriterEnabled
      ? addSceneFirstChapterV1E5C(draft,id,title)
      : addChildrensBookChapterV1E4T({units,outline,id,title});
    if (!result.ok) {setStructureError(result.code);return;}
    setStructureError('');
    onChangeManuscript?.('chapterSceneOutline',result.outline);
  };
  const beginScene = chapterId => {
    if (readOnly || !chapterSections.some(row=>row.key===chapterId)) return;
    if (sceneFirstWriterEnabled) {
      if (!savedAudienceReady) {
        setNotice(fr ? 'Enregistrez d’abord les choix du public dans Book Setup.'
          : 'Save Age Range, Reading Level and Reading Arrangement in Book Setup before writing.');
        return;
      }
      // The Novel-style Write First Scene action is a real writer destination.
      // No physical page, Novel record, or server write is manufactured here.
      const chapter=chapterSections.find(row=>row.key===chapterId);
      const sceneId=newId('cb-scene');
      const sceneTitle=(fr?'Scène ':'Scene ')+((chapter?.scenes?.length||0)+1);
      const result=addSceneFirstSceneV1E5C(draft,chapterId,sceneId,sceneTitle);
      if (!result.ok) {setStructureError(result.code);return;}
      setStructureError('');setNewSceneChapterId('');setNotice('');
      onChangeManuscript?.('chapterSceneOutline',result.outline);
      setDestination({stage:'scene-first-writer',chapterId,sceneId});setChooser(null);
      return;
    }
    setNewSceneChapterId(chapterId);
    setNewSceneUnitId('');setNewSceneTitle('');setStructureError('');
  };
  const renameChapter = ({chapterId,title}) => {
    if(readOnly || !sceneFirstWriterEnabled) return false;
    const result=renameSceneFirstChapterV1E6A(draft,chapterId,title);
    if(!result.ok) {setStructureError(result.code);return false;}
    setStructureError('');
    onChangeManuscript?.('chapterSceneOutline',result.outline);
    return true;
  };
  const moveChapter = ({chapterId,toIndex}) => {
    if(readOnly || !sceneFirstWriterEnabled) return false;
    const result=moveSceneFirstChapterV1E6A(draft,chapterId,toIndex);
    if(!result.ok) {setStructureError(result.code);return false;}
    setStructureError('');
    onChangeManuscript?.('chapterSceneOutline',result.outline);
    return true;
  };
  const commitScene = () => {
    if (readOnly || !newSceneChapterId) return;
    const chapter = chapterSections.find(row=>row.key===newSceneChapterId);
    const id=newId('cb-scene');
    const title=clean(newSceneTitle) || (fr?'Scène ':'Scene ') + ((chapter?.scenes?.length||0)+1);
    const result = sceneFirstWriterEnabled
      ? addSceneFirstSceneV1E5C(draft,newSceneChapterId,id,title)
      : addChildrensBookSceneV1E4T({units,outline,chapterId:newSceneChapterId,id,title,unitId:newSceneUnitId});
    if (!result.ok) {setStructureError(result.code);return;}
    setStructureError('');setNewSceneChapterId('');setNewSceneUnitId('');setNewSceneTitle('');
    onChangeManuscript?.('chapterSceneOutline',result.outline);
  };
  const leaveBook=()=>{
    if(hasUnsavedChanges && typeof window!=='undefined' &&
        !window.confirm(fr?'Des modifications ne sont pas enregistrées. Quitter ?':'Unsaved changes. Leave?')) return;
    if (typeof onExit === 'function') onExit();
    else onOpenExistingWorkspace?.('project-plan');
  };
  if (outline?.version === 2 && !sceneFirstWriterEnabled) return <section className="ns-card" role="alert">
    <h2>{fr?'Mise en pages en cours de vérification':'Layout reconciliation required'}</h2>
    <p>{fr?'Le texte existant est préservé. Aucune conversion automatique ne sera effectuée.'
      :'Existing text is preserved. No automatic page conversion will occur.'}</p>
  </section>;
  if (!navigation?.ok) return (
    <section className="ns-card" role="alert" style={{padding:20}}>
      <h2>{fr?'Structure des pages non valide':'Invalid page navigation'}</h2>
      <p>{fr?'Les données du livre ne sont pas modifiées.':'No book data was modified.'}</p>
      <button className="ns-button ns-button-outline" type="button"
        onClick={()=>onOpenExistingWorkspace?.('manuscript-plan')}>
        {fr?'Ouvrir le manuscrit original':'Open Original Manuscript'}
      </button>
    </section>
  );
  const sceneFirstDestination = destination?.stage === 'scene-first-writer' ? destination : null;
  const sceneFirstChapter = sceneFirstDestination
    ? chapterSections.find(row=>row.key===sceneFirstDestination.chapterId) : null;
  const sceneFirstScene = sceneFirstChapter?.scenes?.find(row=>row.key===sceneFirstDestination?.sceneId);
  const sceneFirstStudioView = sceneFirstDestination && sceneFirstScene ?
    <ChildrensBookSceneFirstWriterV1E5C
      lang={lang} draft={draft} chapter={sceneFirstChapter} scene={sceneFirstScene}
      readOnly={readOnly} savedAudienceReady={savedAudienceReady}
      onTextChange={(id,content)=>{
        const result=changeSceneFirstTextV1E5C(draft,id,content);
        if(result.ok) onChangeManuscript?.('sceneDrafts',result.sceneDrafts);
        else setStructureError(result.code);
      }}
      onReturn={returnToBook}/>:null;
  const chooserView = chooser ? <section className="ns-card" data-cb-v1e4d-chooser="true"
      style={{padding:22,borderRadius:18}}>
    <h2>{fr?'Choisir une page ou double page':'Choose a Page or Spread'}</h2>
    <p>{fr?'Une scène peut contenir plusieurs pages originales.':'A Scene may contain more than one original page or spread.'}</p>
    <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
      {chooser.choices.map(choice=><button key={choice.unitId} className="ns-button ns-button-primary"
        type="button" onClick={()=>openUnit(choice.unitId)}>{choice.pageLabel}{choice.title?' · '+choice.title:''}</button>)}
    </div>
    <button type="button" className="ns-button ns-button-outline" style={{marginTop:14}}
      onClick={returnFromChooser}>{chooserChapter
        ? (fr ? `← Retour à ${chooserChapter.title || 'ce chapitre'}`
              : `← Back to ${chooserChapter.title || 'Chapter'}`)
        : (fr?'← Retour au livre':'← Back to Book')}</button>
  </section> : null;
  const studioView = destination?.stage === 'writer-studio' ? <main data-cb-v1e4d-novel-writer-reuse="true">
    <NextScenesBookBlueprintNavigator
      currentWorkspace={studioStage} projectTitle={title}
      blueprintLabel={fr?'PARCOURS WRITER STUDIO':'WRITER STUDIO PATH'}
      workspaces={[
        {id:'write',label:fr?'Écrire':'Write',status:canWrite?'in_progress':'waiting',disabled:!canWrite},
        {id:'review',label:fr?'Réviser':'Review',status:'ready'},
        {id:'collaboration',label:fr?'Collaborer':'Collaborate',status:'waiting',disabled:true},
        {id:'memory',label:fr?'Soutien de l’histoire':'Story Support',status:'waiting',disabled:true},
      ]}
      onNavigate={stage=>{if(stage==='write'&&!canWrite)return;if(stage==='write'||stage==='review')setStudioStage(stage);}}
      readyCount={canWrite ? 2 : 1} statusTotal={2}
      isFR={fr} accent="#047857" accentDark="#065f46" accentSoft="#ecfdf5"
      border="rgba(4,120,87,0.24)"
      background="linear-gradient(180deg,rgba(236,253,245,0.96),#fff)"
    />
    <section className="ns-card ws-current-work-card" data-writer-studio-current-work="true"
      style={{padding:18,borderRadius:20,marginBottom:16}}>
      <div className="ns-card-meta" style={{fontWeight:900,color:'#047857'}}>{fr?'TRAVAIL EN COURS':'CURRENT WORK'}</div>
      <h2 className="ns-section-title ws-current-work-title">{title}</h2>
      <p style={{margin:'8px 0',fontWeight:700}}>
        {activeChapter?.title || (destination.returnTo?.sectionKind==='front'
          ? (fr?'Pages liminaires':'Front Matter') : destination.returnTo?.sectionKind==='back'
          ? (fr?'Pages finales':'Back Matter') : (fr?'Page originale':'Original Page'))}
      </p>
      {activeChapter ? <div data-cb-v1e4f-chapter-navigation="true"
          style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap',marginBottom:12}}>
        <button className="ns-button ns-button-outline" type="button"
          disabled={chapterIndex<=0} onClick={()=>navigateToChapter(chapterSections[chapterIndex-1]?.key)}>
          {fr?'← Chapitre précédent':'← Previous Chapter'}</button>
        <label style={{display:'grid',gap:4,minWidth:220,flex:'1 1 260px',fontWeight:700}}>
          {fr?'Aller au chapitre':'Jump to Chapter'}
          <select aria-label={fr?'Aller au chapitre':'Jump to Chapter'}
            value={activeChapter.key} onChange={event=>navigateToChapter(event.target.value)}
            style={{minHeight:42,padding:8,border:'1px solid #cbd5e1',borderRadius:10}}>
            {chapterSections.map(row=><option key={row.key} value={row.key}>{row.title || row.key}</option>)}
          </select>
        </label>
        <button className="ns-button ns-button-outline" type="button"
          disabled={chapterIndex<0 || chapterIndex>=chapterSections.length-1}
          onClick={()=>navigateToChapter(chapterSections[chapterIndex+1]?.key)}>
          {fr?'Chapitre suivant →':'Next Chapter →'}</button>
      </div> : null}
      <div style={{display:'flex',alignItems:'end',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
        {activeScene && activeScenes.length ? <label style={{display:'grid',gap:4,minWidth:190,flex:'1 1 220px',fontWeight:700}}>
          {fr?'Scène':'Scene'}
          <select aria-label={fr?'Aller à la scène':'Jump to Scene'} value={activeScene.key}
            onChange={event=>openScene({chapterId:activeChapter.key,sceneId:event.target.value})}
            style={{minHeight:42,padding:8,border:'1px solid #cbd5e1',borderRadius:10}}>
            {activeScenes.map(row=><option key={row.key} value={row.key}>{row.title||row.key}</option>)}
          </select>
        </label> : null}
        <strong>{[destination.pageLabel || (fr?'Page ou double page':'Page or Spread'),
          destination.title].filter(Boolean).join(' · ')}</strong>
        <button className="ns-button ns-button-outline" type="button" onClick={returnToBook}>
          {returnLabel}
        </button>
      </div>
    </section>
    <div data-cb-v1e4d-original-editor="true">
      <ChildrensBookManuscriptPlan
        lang={lang} key={destination.unitId} readOnly={!canWrite||studioStage==='review'}
        novelWritingStyle={true} projectPlan={draft} bookMap={draft.bookMap}
        manuscriptPlan={draft.manuscriptPlan} childrensBookAssets={childrensBookAssets}
        onLoadChildrensBookAssetFile={onLoadChildrensBookAssetFile}
        initialUnitId={destination.unitId}
        focusedUnitId={destination.unitId}
        onChange={(field,value)=>{if(canWrite&&studioStage==='write')onChangeManuscript?.(field,value);}}
        onPositionChange={position=>{
          const id=clean(position?.unitId);
          if(id && units.some(unit=>clean(unit?.id)===id)){
            lastUnitId.current=id;
            onPositionChange?.(position);
          }
        }}
        onReturn={returnToBook} onExit={returnToBook}
      />
    </div>
  </main> : null;
  return <div data-cb-v1e4d-real-novel-book-view="true">
    <div style={{display:destination||chooser?'none':undefined}}>
      {notice?<p role="alert">{notice}</p>:null}
      {!savedAudienceReady ? <p role="note" data-cb-audience-setup-required="true">
        {fr ? "Avant d’écrire une scène, enregistrez la tranche d’âge, le niveau de lecture et le mode de lecture dans Book Setup."
            : "Before writing a Scene, save Age Range, Reading Level and Reading Arrangement in Book Setup."}
      </p> : null}
      <section className="ns-card" data-cb-v1e4t-author-structure="true" style={{padding:16,marginBottom:12}}>
        <p className="ns-card-meta" style={{marginTop:0}}>
          {fr?'Enregistrez vos chapitres et scènes avec « Enregistrer » ci-dessus.'
              :'Use Save Changes above to save your Chapters and Scenes.'}
        </p>
        {sceneFirstWriterEnabled ? <p className="ns-card-meta" data-cb-scene-first-no-page-required="true">
          {fr?'Écrivez vos scènes maintenant. La mise en pages et les illustrations viendront ensuite.'
              :'Write your Scenes now. Arrange pages and illustrations later.'}</p>
          : <>
            <button type="button" className="ns-button ns-button-outline"
              onClick={()=>onOpenExistingWorkspace?.('book-map')}>
              {fr?'Ajouter des pages et doubles pages':'Add Pages & Spreads'}
            </button>
            {!unassignedUnits.length && !readOnly ? <p className="ns-card-meta">
              {fr?'Ajoutez une page du manuscrit avant de créer une scène.'
                :'Add a manuscript page or spread before creating a Scene.'}</p> : null}
          </>}
        {structureError ? <p role="alert">{structureError}</p> : null}
        {newSceneChapterId && !readOnly ? <div data-cb-v1e4t-new-scene="true"
          style={{display:'grid',gap:10,marginTop:12}}>
          <h3>{fr?'Ajouter une scène':'Add Scene'}</h3>
          <label>{fr?'Titre de la scène':'Scene title'}
            <input className="ns-input" value={newSceneTitle}
              onChange={event=>setNewSceneTitle(event.target.value)}/>
          </label>
          {!sceneFirstWriterEnabled ? <label>{fr?'Page ou double page existante':'Existing manuscript page or spread'}
            <select className="ns-input" value={newSceneUnitId}
              onChange={event=>setNewSceneUnitId(event.target.value)}>
              <option value="">{fr?'Choisir une page':'Choose a page'}</option>
              {unassignedUnits.map(item=><option key={item.unitId} value={item.unitId}>
                {item.pageLabel}{item.title?' · '+item.title:''}</option>)}
            </select>
          </label> : null}
          <div style={{display:'flex',gap:8}}>
            <button className="ns-button ns-button-primary" type="button"
              disabled={!sceneFirstWriterEnabled && !newSceneUnitId} onClick={commitScene}>
              {fr?'Ajouter au brouillon':'Add Scene'}</button>
            <button className="ns-button ns-button-outline" type="button"
              onClick={()=>{setNewSceneChapterId('');setStructureError('');}}>
              {fr?'Annuler':'Cancel'}</button>
          </div>
        </div> : null}
      </section>
      {!sceneFirstWriterEnabled ? <details data-cb-v1e5-secondary-book-tools="true"
        style={{padding:14,margin:"12px 0",border:"1px solid #d1d5db",borderRadius:12}}>
        <summary style={{cursor:"pointer",fontWeight:800}}>
          {fr ? "Autres outils du livre" : "More Book Tools"}
        </summary>
        <div style={{display:"flex",flexWrap:"wrap",gap:10,marginTop:12}}>
          {[
            ["illustration-plan",fr ? "Illustrations" : "Illustrations"],
            ["cover-plan",fr ? "Couverture" : "Cover"],
            ["book-assembly",fr ? "Relire le livre" : "Whole-Book Review"],
            ["publication-readiness",fr ? "Préparer la publication" : "Publication Preparation"],
          ].map(([destination,label])=>(
            <button className="ns-button ns-button-outline" type="button"
              key={destination} onClick={()=>onOpenExistingWorkspace?.(destination)}>
              {label}
            </button>
          ))}
        </div>
      </details> : null}
      <ChildrensBookNovelBookViewReuseV1E4C
        childrensBookMatterCounts={{
          front: Array.isArray(draft?.manuscriptPlan?.bookMatterDrafts?.front)
            ? draft.manuscriptPlan.bookMatterDrafts.front.length : 0,
          back: Array.isArray(draft?.manuscriptPlan?.bookMatterDrafts?.back)
            ? draft.manuscriptPlan.bookMatterDrafts.back.length : 0,
        }}
        onChildrensBookRenderMatter={kind=><ChildrensBookSceneFirstMatterV1E6B2
          kind={kind} draft={draft} lang={lang} readOnly={readOnly}
          onChangeMatter={matter=>onChangeManuscript?.('bookMatterDrafts',matter)}/>}
        onChildrensBookAddChapter={addChapter}
        onChildrensBookAddScene={beginScene}
        onChildrensBookRenameChapter={renameChapter}
        onChildrensBookMoveChapter={moveChapter}
        draft={draft} navigation={navigation} lang={lang}
        onChildrensBookBookSetup={()=>onOpenExistingWorkspace?.("project-plan-audience-v1e4x")}
        childrensBookAudienceReady={savedAudienceReady}
        returnTarget={bookReturnTarget}
        onBack={leaveBook} onOpenExistingScene={openScene}
        onUnsupportedStructureChange={()=>setNotice(fr
          ?'La gestion des chapitres et scènes nécessite une sauvegarde vérifiée.'
          :'Chapter/Scene management is unavailable until persistence is verified.')}
      />
      {!sceneFirstWriterEnabled ? <details data-cb-v1e4g-secondary-pages="true" style={{marginTop:16}}>
        <summary style={{cursor:'pointer',fontWeight:800}}>
          {fr?'Pages et doubles pages originales':'Original Pages & Spreads'}
        </summary>
        <ChildrensBookChapterSceneNavigatorV1
          units={units} chapters={savedChapters} lang={lang} onOpenUnit={openUnit}/>
      </details> : null}
    </div>
    {chooserView}
    {studioView}
    {sceneFirstStudioView}
  </div>;
}
